import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Person, EventDto } from "./types";
import { FunctionOption } from "./types";
import "./../Event/AdminEvent.css";

interface Country {
    code: string;
}

type PersonFormData = Omit<Person, "functions"> & {
    functions: FunctionOption[];
    uploadImageBase64?: string;
};

interface Props {
    initialPerson?: Person | null;
    onSave: (person: PersonFormData) => Promise<void> | void;
    onCancel: () => void;
    allEvents: EventDto[];
    functions: FunctionOption[];
    currentUserId: number;
    duplicateCandidates?: Person[];
    onUseExisting?: (person: Person) => void;
    onForceCreate?: () => void;
}

const PersonModalForm: React.FC<Props> = ({
    initialPerson,
    onSave,
    onCancel,
    allEvents,
    functions,
    currentUserId,
    duplicateCandidates = [],
    onUseExisting,
    onForceCreate,
}) => {
    const isEdit = !!initialPerson?.personId;

    const [countries, setCountries] = useState<Country[]>([]);
    const [busy, setBusy] = useState(false);
    const safeDuplicateCandidates = duplicateCandidates ?? [];
    const [formData, setFormData] = useState<PersonFormData>(() => ({
        personId: initialPerson?.personId ?? 0,
        userId: initialPerson?.userId ?? currentUserId,
        firstName: initialPerson?.firstName ?? "",
        lastName: initialPerson?.lastName ?? "",
        dateOfBirth: initialPerson?.dateOfBirth,
        country: initialPerson?.country ?? "",
        title: initialPerson?.title ?? "",
        company: initialPerson?.company ?? "",
        personImagePath: initialPerson?.personImagePath ?? "",
        profileUrl: initialPerson?.profileUrl ?? "",
        events: initialPerson?.events ?? [],
        functions:
            initialPerson?.functions?.map((f) => ({
                functionId: f.functionId,
                functionName: f.functionName,
                eventID: f.eventId,
                categoryName: "",
            })) ?? [],
        uploadImageBase64: undefined,
        created: initialPerson?.created ?? new Date().toISOString(),
        updated: initialPerson?.updated,
        zones: initialPerson?.zones ?? [],
        hasProfileImage: initialPerson?.hasProfileImage ?? false,
    }));

    const [previewImage, setPreviewImage] = useState<string | undefined>(
        initialPerson?.personImagePath || initialPerson?.profileUrl || undefined
    );
    const [activeEventId, setActiveEventId] = useState<number | null>(null);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/countries`);
                setCountries(res.data);
            } catch (err) {
                console.error("Failed to load countries", err);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        setFormData({
            personId: initialPerson?.personId ?? 0,
            userId: initialPerson?.userId ?? currentUserId,
            firstName: initialPerson?.firstName ?? "",
            lastName: initialPerson?.lastName ?? "",
            dateOfBirth: initialPerson?.dateOfBirth,
            country: initialPerson?.country ?? "",
            title: initialPerson?.title ?? "",
            company: initialPerson?.company ?? "",
            personImagePath: initialPerson?.personImagePath ?? "",
            profileUrl: initialPerson?.profileUrl ?? "",
            events: initialPerson?.events ?? [],
            functions:
                initialPerson?.functions?.map((f) => ({
                    functionId: f.functionId,
                    functionName: f.functionName,
                    eventID: f.eventId,
                    categoryName: "",
                })) ?? [],
            uploadImageBase64: undefined,
            created: initialPerson?.created ?? new Date().toISOString(),
            updated: initialPerson?.updated,
            zones: initialPerson?.zones ?? [],
            hasProfileImage: initialPerson?.hasProfileImage ?? false,
        });

        setPreviewImage(
            initialPerson?.personImagePath || initialPerson?.profileUrl || undefined
        );
    }, [initialPerson, currentUserId]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onCancel();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onCancel]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const compressImage = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const img = new Image();

                img.onload = () => {
                    const maxWidth = 600;
                    const maxHeight = 600;
                    const quality = 0.7;

                    let { width, height } = img;

                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width = Math.round(width * ratio);
                        height = Math.round(height * ratio);
                    }

                    const canvas = document.createElement("canvas");
                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        reject(new Error("Kunne ikke oprette canvas context"));
                        return;
                    }

                    ctx.drawImage(img, 0, 0, width, height);

                    const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
                    resolve(compressedBase64);
                };

                img.onerror = () => reject(new Error("Kunne ikke indlæse billede"));
                img.src = reader.result as string;
            };

            reader.onerror = () => reject(new Error("Kunne ikke læse fil"));
            reader.readAsDataURL(file);
        });
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setBusy(true);

            const compressedImage = await compressImage(file);

            setPreviewImage(compressedImage);
            setFormData((prev) => ({
                ...prev,
                uploadImageBase64: compressedImage,
            }));
        } catch (error) {
            console.error("Fejl ved behandling af billede", error);
            alert("Der opstod en fejl ved behandling af billedet.");
        } finally {
            setBusy(false);
        }
    };

    const removeTag = (field: "events" | "functions", idx: number) => {
        setFormData((prev) => ({
            ...prev,
            [field]: (prev[field] as any[]).filter((_, i) => i !== idx),
        }));
    };

    const addEvent = (ev: EventDto) => {
        setFormData((prev) => {
            if ((prev.events ?? []).some((e) => e.eventID === ev.eventID)) return prev;
            return { ...prev, events: [...(prev.events ?? []), ev] };
        });
    };

    const addFunction = (fn: FunctionOption) => {
        setFormData((prev) => {
            const existing = prev.functions ?? [];

            if (existing.some(f => f.functionId === fn.functionId)) {
                return prev;
            }

            return {
                ...prev,
                functions: [...existing, fn],
            };
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            alert("Fornavn og efternavn er påkrævet");
            return;
        }

        const payload: PersonFormData = {
            ...formData,
            userId: isEdit ? formData.userId : currentUserId,
            created: isEdit ? formData.created : new Date().toISOString(),
            events: formData.events ?? [],
            functions: formData.functions ?? [],
        };

        try {
            setBusy(true);

            // 🔥 NEW: let parent decide duplicates
            await onSave(payload);

        } finally {
            setBusy(false);
        }
    };


    const filteredFunctions = activeEventId
        ? functions.filter((f) => f.eventID === activeEventId)
        : [];

    const removeFunctionChip = (idx: number) => {
        setFormData(prev => ({
            ...prev,
            functions: prev.functions.filter((_, i) => i !== idx),
        }));
    };

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="eventform-card">
                    <div className="eventform-header">
                        <h2 className="eventform-title">
                            {isEdit ? "Rediger Person" : "Tilføj Person"}
                        </h2>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="eventform-grid">
                            <div className="field">
                                <label>Fornavn</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="field">
                                <label>Efternavn</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="field">
                                <label>Fødselsdato</label>
                                <input
                                    type="date"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth ? formData.dateOfBirth.split("T")[0] : ""}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="field">
                                <label>Land</label>
                                <select
                                    name="country"
                                    value={formData.country || ""}
                                    onChange={handleChange}
                                    className="styled-dropdown"
                                >
                                    <option value="">Vælg land</option>
                                    {countries.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.code}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="field">
                                <label>Title</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title || ""}
                                    onChange={handleChange}
                                    placeholder="e.g. Manager"
                                />
                            </div>

                            <div className="field">
                                <label>Company</label>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company || ""}
                                    onChange={handleChange}
                                    placeholder="e.g. Microsoft"
                                />
                            </div>

                            <div className="field" style={{ gridColumn: "1 / -1" }}>
                                <label>Events</label>
                                <div className="chip-row">
                                    {(formData.events ?? []).map((ev, idx) => (
                                        <span
                                            className={`chip ${activeEventId === ev.eventID ? "chip-active" : ""}`}
                                            key={ev.eventID}
                                            onClick={() => setActiveEventId(ev.eventID)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <span className="chip-text">{ev.eventName}</span>

                                            <button
                                                type="button"
                                                className="chip-x"
                                                onClick={(e) => {
                                                    e.stopPropagation(); // IMPORTANT so click doesn't activate chip
                                                    removeTag("events", idx);

                                                    // if removing active chip → reset
                                                    if (activeEventId === ev.eventID) {
                                                        setActiveEventId(null);
                                                    }
                                                }}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <select
                                    className="styled-dropdown"
                                    defaultValue=""
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value) return;

                                        const ev = allEvents.find(x => x.eventID === Number(value));
                                        if (ev) {
                                            addEvent(ev);
                                            setActiveEventId(ev.eventID); // ✅ THIS IS REQUIRED
                                        }

                                        e.target.value = "";
                                    }}
                                >
                                    <option value="">Tilføj event</option>
                                    {allEvents
                                        .filter(
                                            (ev) =>
                                                !(formData.events ?? []).some(
                                                    (s) => s.eventID === ev.eventID
                                                )
                                        )
                                        .map((ev) => (
                                            <option key={ev.eventID} value={ev.eventID}>
                                                {ev.eventName}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="field">
                                <label>Funktioner</label>
                                <div className="chip-row">
                                    {(formData.functions ?? [])
                                        .filter((fn) => {
                                            if (!activeEventId) return true;
                                            return Number(fn.eventID) === Number(activeEventId);
                                        })
                                        .map((fn, idx) => (
                                        <span className="chip" key={fn.functionId ?? idx}>
                                            <span className="chip-text">
                                                {fn.functionName}
                                            </span>

                                            <button
                                                type="button"
                                                className="chip-x"
                                                onClick={() => removeFunctionChip(idx)}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <select
                                    className="styled-dropdown"
                                    defaultValue=""
                                    disabled={!activeEventId}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        if (!value) return;

                                        const fn = functions.find(f => f.functionId === value);
                                        if (fn) addFunction(fn);

                                        e.target.value = "";
                                    }}
                                >
                                    <option value="">
                                        {(formData.events ?? []).length === 0
                                            ? "Vælg event først"
                                            : "For at tilføje funktion, Vælg event først"}
                                    </option>

                                    {filteredFunctions
                                        .filter(
                                            (fn) =>
                                                !(formData.functions ?? []).some(
                                                    (f) => f.functionId === fn.functionId
                                                )
                                        )
                                        .map((fn) => (
                                            <option key={fn.functionId} value={fn.functionId}>
                                                {fn.functionName} ({fn.categoryName ?? "No category"})
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="field">
                                <label>Profilbillede</label>

                                {previewImage && (
                                    <div style={{ marginBottom: 10 }}>
                                        <img
                                            src={previewImage}
                                            alt="Preview"
                                            style={{
                                                width: 120,
                                                height: 120,
                                                objectFit: "cover",
                                                borderRadius: "50%",
                                            }}
                                        />
                                    </div>
                                )}

                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={handleImageChange}
                                />
                            </div>
                        </div>
                        {safeDuplicateCandidates.length > 0 && (
                            <div className="duplicateBox">
                                <div className="duplicateHeader">
                                    <h3>Mulige dubletter fundet</h3>
                                    <p>Vi fandt personer der ligner det du prøver at oprette</p>
                                </div>

                                <div className="duplicateList">
                                    {safeDuplicateCandidates.map((p) => (
                                        <div key={p.personId} className="duplicateCard">
                                            <div className="duplicateInfo">
                                                <div className="duplicateName">
                                                    {p.firstName} {p.lastName}
                                                </div>

                                                <div className="duplicateMeta">
                                                    {p.company && <span>{p.company}</span>}
                                                    {p.company && p.title && <span className="dot">•</span>}
                                                    {p.title && <span>{p.title}</span>}
                                                </div>
                                            </div>

                                            <div className="duplicateActions">
                                                <button
                                                    type="button"
                                                    className="btn btn--default"
                                                    onClick={() => onUseExisting?.(p)}
                                                >
                                                    Brug eksisterende
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="duplicateFooter">
                                    <button
                                        type="button"
                                        className="btn btn--secondary"
                                        onClick={onForceCreate}
                                    >
                                        Opret alligevel
                                    </button>

                                    <span className="duplicateHint">
                                        Eller vælg en eksisterende person ovenfor
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="eventform-actions">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={onCancel}
                                disabled={busy}
                            >
                                Luk
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={busy}>
                                {busy ? "Gemmer..." : "Gem"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PersonModalForm;